import {
  CashIcon,
  CreditCardIcon,
  MoneyIcon,
  PixIcon,
  WalletIcon,
  BarcodeIcon,
  TransferRealIcon,
  ExternalLinkIcon,
} from '@nimbus-ds/icons';
import { TFunction } from 'i18next';
import {
  PaymentProviderOptionsResponseDto,
  CheckoutPaymentOptionDto,
  PaymentOptionInfoDto,
  PromotionResponseDto,
} from '@tiendanube/common';
import { Icon } from '@tiendanube/icons/dist/icons';
import { isNuvemPagoProvider } from 'commons/utils';
import { isPercentageOffPromotion } from 'domains/Marketing/Promotions/utils/promotionUtils';

export function getPaymentOptionIcon(translationKey: string): Icon {
  switch (translationKey) {
    case 'credit_card':
    case 'debit_card':
    case 'credit_and_debit_card':
      return CreditCardIcon;
    case 'wallet':
      return WalletIcon;
    case 'pix':
      return PixIcon;
    case 'cash':
      return CashIcon;
    case 'wire_transfer':
    case 'bank_debit':
      return TransferRealIcon;
    case 'boleto':
    case 'ticket':
      return BarcodeIcon;
    case 'redirect':
      return ExternalLinkIcon;
    case 'other':
    default:
      return MoneyIcon;
  }
}

export function buildPaymentOptionDescription(
  translationKey: string,
  description: string,
  paymentProviderName: string,
  t: TFunction,
  withPaymentProviderName = true,
): string {
  let text: string;
  if (translationKey === 'redirect') {
    text = description;
  } else {
    text = t(`paymentMethodTypes.${translationKey}`);
  }

  return withPaymentProviderName ? `${paymentProviderName} - ${text}` : text;
}

export function hasAnyPaymentOptionDisabled(
  activePaymentOptions: PaymentProviderOptionsResponseDto[],
): boolean {
  return activePaymentOptions.some((paymentProvider) =>
    paymentProvider.checkoutPaymentOptions.some(
      (option) => option.legacyDiscount > 0,
    ),
  );
}

export function findMatchingPaymentProvider(
  options: PaymentProviderOptionsResponseDto[],
  optionId: string,
): PaymentProviderOptionsResponseDto | undefined {
  return options.find((paymentProvider) =>
    paymentProvider.checkoutPaymentOptions.some((opt) => opt.id === optionId),
  );
}

export function buildPaymentOptionInfo(
  paymentProvider: PaymentProviderOptionsResponseDto,
  option: CheckoutPaymentOptionDto,
): PaymentOptionInfoDto {
  return {
    id: option.id,
    description: option.name,
    translationKey: option.translationKey,
    paymentProviderName: paymentProvider.paymentProviderName,
  };
}

export const promotionHasLegacyDiscountConflict = (
  promotion: PromotionResponseDto,
  activePaymentOptions: PaymentProviderOptionsResponseDto[],
): boolean => {
  if (!isPercentageOffPromotion(promotion)) {
    return false;
  }

  return (
    promotion.paymentOptions?.some((paymentOption: PaymentOptionInfoDto) =>
      paymentOptionHasLegacyDiscount(paymentOption, activePaymentOptions),
    ) ?? false
  );
};

export const paymentOptionHasLegacyDiscount = (
  paymentOption: PaymentOptionInfoDto,
  activePaymentOptions: PaymentProviderOptionsResponseDto[],
): boolean =>
  activePaymentOptions.some((provider) =>
    provider.checkoutPaymentOptions.some(
      (activeOption) =>
        activeOption.id === paymentOption.id && activeOption.legacyDiscount > 0,
    ),
  );

export function filterNuvemPagoOptions(
  paymentOptions: PaymentOptionInfoDto[],
  activePaymentOptions: PaymentProviderOptionsResponseDto[],
): PaymentOptionInfoDto[] {
  return paymentOptions.filter((paymentOption) => {
    const matchingProvider = activePaymentOptions.find((provider) =>
      provider.checkoutPaymentOptions.some(
        (option) => option.id === paymentOption.id,
      ),
    );

    return (
      matchingProvider &&
      isNuvemPagoProvider(matchingProvider.paymentProviderName)
    );
  });
}
